/**
 * Hooks pour O360-Guard.
 */
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchGuardDashboardSummary,
  fetchGuardAlerts,
  fetchGuardSites,
  fetchGuardShifts,
  fetchGuardIncidents,
  fetchGuardDailyReport,
  exportGuardDailyReport,
  fetchGuardAgents,
  fetchGuardIncidentTypes,
  createGuardShift,
  createGuardIncident,
  incidentTakeOver,
  incidentResolve,
  incidentReject,
  type GuardDashboardSummary,
  type GuardAlerts,
  type GuardSite,
  type GuardShift,
  type GuardIncident,
  type GuardDailyReportData,
  type GuardAgentOption,
  type GuardIncidentType,
  type CreateGuardShiftPayload,
  type CreateIncidentPayload,
} from "../services/tracking/guardService";

export function useGuardDashboard() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data: summary, error: summaryError, isLoading: loadingSummary } = useSWR<GuardDashboardSummary>(
    token ? ["guard-dashboard-summary", token] : null,
    () => fetchGuardDashboardSummary(token),
    { revalidateOnFocus: false }
  );

  const { data: alerts, error: alertsError, isLoading: loadingAlerts } = useSWR<GuardAlerts>(
    token ? ["guard-alerts", token] : null,
    () => fetchGuardAlerts(token),
    { revalidateOnFocus: false }
  );

  return {
    summary: summary ?? {
      agents_on_duty: 0,
      incidents_open: 0,
      patrols_in_progress: 0,
      patrols_planned_today: 0,
      patrols_completed_today: 0,
    },
    alerts: alerts ?? {
      sla_imminent: [],
      sla_depassement: [],
      patrols_missed: [],
      patrols_late: [],
    },
    isLoading: loadingSummary || loadingAlerts,
    error: summaryError || alertsError,
  };
}

export function useGuardSites() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardSite[]>(
    token ? ["guard-sites", token] : null,
    () => fetchGuardSites(token),
    { revalidateOnFocus: false }
  );

  return {
    sites: data ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useGuardShifts() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardShift[]>(
    token ? ["guard-shifts", token] : null,
    () => fetchGuardShifts(token),
    { revalidateOnFocus: false }
  );

  return {
    shifts: data ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useGuardIncidents(params?: { status?: string; site?: string; severity?: string }) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardIncident[]>(
    token ? ["guard-incidents", token, params] : null,
    () => fetchGuardIncidents(token, params),
    { revalidateOnFocus: false }
  );

  return {
    incidents: data ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export function useGuardAgents() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardAgentOption[]>(
    token ? ["guard-agents", token] : null,
    () => fetchGuardAgents(token),
    { revalidateOnFocus: false }
  );

  return {
    agents: data ?? [],
    isLoading: isLoading,
    error,
    refetch: mutate,
  };
}

export function useGuardIncidentTypes() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardIncidentType[]>(
    token ? ["guard-incident-types", token] : null,
    () => fetchGuardIncidentTypes(token),
    { revalidateOnFocus: false }
  );

  return {
    incidentTypes: data ?? [],
    isLoading,
    error,
    refetch: mutate,
  };
}

export async function createGuardShiftMutation(
  accessToken: string | undefined,
  payload: CreateGuardShiftPayload
): Promise<GuardShift> {
  return createGuardShift(accessToken, payload);
}

export async function createGuardIncidentMutation(
  accessToken: string | undefined,
  payload: CreateIncidentPayload,
  files?: File[]
): Promise<GuardIncident> {
  return createGuardIncident(accessToken, payload, files);
}

export async function incidentTakeOverMutation(
  accessToken: string | undefined,
  incidentId: string
): Promise<GuardIncident> {
  return incidentTakeOver(accessToken, incidentId);
}

export async function incidentResolveMutation(
  accessToken: string | undefined,
  incidentId: string,
  payload?: { resolution_comment?: string }
): Promise<GuardIncident> {
  return incidentResolve(accessToken, incidentId, payload);
}

export async function incidentRejectMutation(
  accessToken: string | undefined,
  incidentId: string
): Promise<GuardIncident> {
  return incidentReject(accessToken, incidentId);
}

export function useGuardDailyReport(date?: string) {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, isLoading, error, mutate } = useSWR<GuardDailyReportData>(
    token ? ["guard-daily-report", date ?? "today", token] : null,
    () => fetchGuardDailyReport(token, date),
    { revalidateOnFocus: false }
  );

  return {
    report: data ?? {},
    isLoading,
    error,
    refetch: mutate,
  };
}

export async function exportGuardDailyReportFile(
  accessToken: string | undefined,
  options: { date?: string; format: "pdf" | "xlsx" }
): Promise<Blob> {
  return exportGuardDailyReport(accessToken, options);
}

export type {
  GuardDashboardSummary,
  GuardAlerts,
  GuardSite,
  GuardShift,
  GuardIncident,
  GuardDailyReportData,
  GuardAgentOption,
  GuardIncidentType,
};

